// TODO: Make Pill Component common for all cases.

import dayjs from "dayjs";
import { useMemo } from "react";
import { has, keyBy, upperFirst } from "lodash";
import { Group, Loader, Pill } from "@mantine/core";
import { actions } from "../../../../store/useStore";

export function AppliedFilters({
  appliedFilters,
  filterKeys,
  filterTypes,
  data,
  form,
  customLabels = {},
  valueModifiers = {},
  dependentFilter = {},
  dateFormats = {},
  withRemoveButton = true,
}: {
  data: any;
  form?: any;
  filterKeys: Record<string, string>;
  appliedFilters: Record<string, any>;
  customLabels?: Record<string, string>;
  filterTypes: Record<string, "string" | "number" | "date">;
  valueModifiers?: Record<
    string,
    { prefix?: string; suffix?: string; divider?: string }
  >;
  dependentFilter?: Record<string, string[]>;
  dateFormats?: Record<string, string>;
  withRemoveButton?: boolean;
}) {
  const lookupMaps = useMemo(() => {
    const map: any = {};
    Object.entries(filterKeys).forEach(([filterKey, apiResponseKey]: any) => {
      const nestedKeys = apiResponseKey.split(".");
      let responseData = data;
      nestedKeys.forEach((key: any) => {
        responseData = responseData?.[key] || [];
      });
      map[filterKey] = keyBy(responseData, (item) => String(item.value));
    });
    return map;
  }, [filterKeys, data]);

  const formatDate = (dateString: string, key: string) => {
    const date = dayjs(dateString);
    if (!date.isValid()) return dateString;
    return date.format(dateFormats[key] || "MMM DD, YYYY");
  };

  const formatNumber = (number: string | number) => {
    const numericValue = Number(number);
    if (isNaN(numericValue)) return number;
    return new Intl.NumberFormat().format(numericValue);
  };

  const applyModifiers = (value: string | number, key: string) => {
    const modifier = valueModifiers[key] || {};
    const prefix = modifier.prefix || "";
    const suffix = modifier.suffix || "";
    return `${prefix}${value}${suffix}`;
  };

  const formatMultipleValues = (
    filterKey: string,
    values: any[],
    type: string
  ) => {
    return values.map((value) => {
      const stringValue = String(value);
      const label = lookupMaps[filterKey]?.[stringValue]?.label || stringValue;
      let formattedLabel = label;

      if (type === "date") {
        formattedLabel = formatDate(label, filterKey);
      } else if (type === "number") {
        formattedLabel = formatNumber(label);
      }

      return applyModifiers(formattedLabel, filterKey);
    });
  };

  const getLabelForKey = (key: string) => {
    // Use custom label if provided, otherwise transform the key
    return (
      customLabels[key] ||
      upperFirst(key.replaceAll("_", " ").replace("id", ""))
    );
  };

  const filterPills = Object.entries(appliedFilters).map(([key, value]) => {
    const type = filterTypes[key] || "string";
    const labelForKey = getLabelForKey(key);
    const valueDivider = valueModifiers[key]?.divider || ", ";

    // If filterKey is not provided, use value directly
    if (!filterKeys[key]) {
      if (Array.isArray(value)) {
        const labels = value.map((v) =>
          applyModifiers(
            type === "number"
              ? formatNumber(v)
              : type === "date"
              ? formatDate(v, key)
              : String(v),
            key
          )
        );
        return (
          <Pill
            withRemoveButton={withRemoveButton}
            onRemove={() => {
              actions.filter.remove(key, form);

              if (has(dependentFilter, key)) {
                const dependentFields = Object.keys(dependentFilter?.[key]);

                dependentFields?.forEach((fieldName) => {
                  actions.filter.remove(fieldName, form, type);
                });
              }
            }}
            key={`${key}-${labels.join(valueDivider)}`}
            variant="default"
            h="max-content"
            radius="md"
            bg="white"
            c="#000"
            px={12}
            py={8}
            style={{
              border: "1.5px solid #1A73E8",
            }}
          >
            {`${labelForKey}: ${labels.join(valueDivider)}`}
          </Pill>
        );
      }

      const formattedValue = applyModifiers(
        type === "number"
          ? formatNumber(value)
          : type === "date"
          ? formatDate(value, key)
          : String(value),
        key
      );

      return (
        <Pill
          withRemoveButton={withRemoveButton}
          onRemove={() => {
            actions.filter.remove(key, form, type);

            if (has(dependentFilter, key)) {
              const dependentFields = Object.keys(dependentFilter?.[key]);

              dependentFields?.forEach((fieldName) => {
                actions.filter.remove(fieldName, form);
              });
            }
          }}
          key={key}
          variant="default"
          h="max-content"
          radius="md"
          bg="white"
          c="#000"
          px={12}
          py={8}
          style={{
            border: "1.5px solid #1A73E8",
          }}
        >
          {`${labelForKey}: ${formattedValue}`}
        </Pill>
      );
    }

    if (Array.isArray(value)) {
      const labels = formatMultipleValues(key, value, type);
      const hasRawValues = labels.some(
        (label, index) =>
          !lookupMaps[key]?.[String(value[index])] &&
          !isNaN(Number(label)) &&
          type !== "date"
      );

      return (
        <Pill
          onRemove={() => {
            actions.filter.remove(key, form, type);

            if (has(dependentFilter, key)) {
              const dependentFields = Object.keys(dependentFilter?.[key]);

              dependentFields?.forEach((fieldName) => {
                actions.filter.remove(fieldName, form);
              });
            }
          }}
          withRemoveButton={withRemoveButton}
          key={`${key}-${labels.join(valueDivider)}`}
          variant="default"
          h="max-content"
          radius="md"
          bg="white"
          c="#000"
          px={12}
          py={8}
          style={{
            border: "1.5px solid #1A73E8",
          }}
        >
          {labelForKey}:{" "}
          {hasRawValues ? (
            <Loader size={12} />
          ) : (
            upperFirst(labels.join(valueDivider))
          )}
        </Pill>
      );
    }

    const stringValue = String(value);
    const lookupLabel = lookupMaps[key]?.[stringValue]?.label;
    const label = lookupLabel || stringValue;

    let formattedLabel =
      type === "date" && !lookupLabel
        ? formatDate(label, key)
        : type === "number"
        ? formatNumber(label)
        : label;

    formattedLabel = applyModifiers(formattedLabel, key);

    const isRawValue =
      !lookupLabel && !isNaN(Number(formattedLabel)) && type !== "date";

    return (
      <Pill
        py={8}
        px={12}
        c="#000"
        bg="white"
        radius="md"
        h="max-content"
        variant="default"
        withRemoveButton={withRemoveButton}
        key={`${key}-${stringValue}`}
        onRemove={() => {
          actions.filter.remove(key, form, type);

          if (has(dependentFilter, key)) {
            const dependentFields = Object.values(dependentFilter?.[key]);

            dependentFields?.forEach((fieldName) => {
              actions.filter.remove(fieldName, form, type);
            });
          }
        }}
        style={{
          border: "1.5px solid #1A73E8",
        }}
      >
        {labelForKey}:{" "}
        {isRawValue ? <Loader size={12} /> : upperFirst(formattedLabel)}
      </Pill>
    );
  });

  return <Group gap={12}>{filterPills}</Group>;
}
